import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Star, Gift, TrendingUp, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CustomerSignup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    if (!acceptTerms || !acceptPrivacy) {
      toast.error('Veuillez accepter les conditions d\'utilisation et la politique de confidentialité');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/api/customers`, formData);
      toast.success('Inscription réussie ! Vous pouvez maintenant bénéficier du programme de fidélité.');
      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Un compte existe déjà avec cet email');
      } else {
        toast.error('Erreur lors de l\'inscription');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black">
      <div className="container mx-auto px-6 py-12">
        {/* Back Button */}
        <Button
          onClick={() => navigate('/')}
          variant="outline"
          className="border-gold/30 text-gold hover:bg-gold/10 mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour à l'accueil
        </Button>

        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Rejoignez le Programme <span className="text-gold">Fidélité</span>
            </h1>
            <p className="text-xl text-gray-400">
              Inscrivez-vous gratuitement et profitez de réductions exclusives
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Benefits */}
            <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-gold/30">
              <CardHeader>
                <CardTitle className="text-2xl text-gold flex items-center gap-2">
                  <Gift className="w-6 h-6" />
                  Avantages du Programme
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-gold/20 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg mb-1">Programme de Fidélité</h3>
                    <p className="text-gray-400">
                      <strong className="text-gold">5 courses = 10% de réduction</strong> sur votre 6ème course !
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-purple-500/20 rounded-lg">
                    <Star className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg mb-1">Accès VIP</h3>
                    <p className="text-gray-400">
                      Possibilité de devenir client VIP avec des réductions permanentes
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-500/20 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg mb-1">Réservations Rapides</h3>
                    <p className="text-gray-400">
                      Vos informations pré-remplies pour réserver en quelques clics
                    </p>
                  </div>
                </div>

                <div className="bg-gold/10 border border-gold/30 rounded-lg p-4 mt-6">
                  <p className="text-white text-center font-semibold">
                    🎁 Inscription 100% gratuite
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Signup Form */}
            <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-gold/30">
              <CardHeader>
                <CardTitle className="text-2xl text-white">
                  Créer mon compte
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-white text-base">
                      Nom complet *
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-gray-800 border-gold/30 text-white text-lg"
                      placeholder="Jean Dupont"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-white text-base">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-gray-800 border-gold/30 text-white text-lg"
                      placeholder="jean.dupont@example.com"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-white text-base">
                      Téléphone *
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="bg-gray-800 border-gold/30 text-white text-lg"
                      placeholder="+32 470 12 34 56"
                      required
                    />
                  </div>

                  {/* RGPD Consents */}
                  <div className="space-y-4 bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                    <p className="text-white font-semibold text-sm mb-3">📋 Consentements RGPD (obligatoires)</p>
                    
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="acceptTerms"
                        checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.target.checked)}
                        className="mt-1 w-4 h-4 accent-amber-500"
                        required
                      />
                      <label htmlFor="acceptTerms" className="text-gray-300 text-sm">
                        J'accepte les{' '}
                        <button
                          type="button"
                          onClick={() => window.open('/terms', '_blank')}
                          className="text-amber-500 hover:text-amber-400 underline"
                        >
                          Conditions Générales d'Utilisation
                        </button>{' '}
                        *
                      </label>
                    </div>

                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="acceptPrivacy"
                        checked={acceptPrivacy}
                        onChange={(e) => setAcceptPrivacy(e.target.checked)}
                        className="mt-1 w-4 h-4 accent-amber-500"
                        required
                      />
                      <label htmlFor="acceptPrivacy" className="text-gray-300 text-sm">
                        J'accepte la{' '}
                        <button
                          type="button"
                          onClick={() => window.open('/privacy-policy', '_blank')}
                          className="text-amber-500 hover:text-amber-400 underline"
                        >
                          Politique de Confidentialité
                        </button>{' '}
                        et le traitement de mes données personnelles conformément au RGPD *
                      </label>
                    </div>

                    <p className="text-xs text-gray-500 mt-2">
                      🔒 Vos données sont protégées et ne seront jamais vendues à des tiers.
                    </p>
                  </div>

                  <div className="bg-gray-800/50 border border-gold/30 rounded-lg p-4">
                    <p className="text-gray-400 text-sm">
                      En vous inscrivant, vous acceptez de recevoir des informations sur nos services et promotions.
                    </p>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-gold to-amber-600 text-black text-lg py-6 hover:from-amber-600 hover:to-gold"
                  >
                    {loading ? 'Inscription...' : 'S\'inscrire Gratuitement'}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-gray-400 text-sm">
                    Déjà inscrit ? Utilisez votre email lors de votre prochaine réservation
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* How it works */}
          <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-gold/30">
            <CardHeader>
              <CardTitle className="text-2xl text-white text-center">
                Comment ça fonctionne ?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl font-bold text-gold">1</span>
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">Inscrivez-vous</h3>
                  <p className="text-gray-400">
                    Créez votre compte gratuitement en quelques secondes
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl font-bold text-gold">2</span>
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">Réservez</h3>
                  <p className="text-gray-400">
                    Utilisez votre email lors de vos réservations
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl font-bold text-gold">3</span>
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">Bénéficiez</h3>
                  <p className="text-gray-400">
                    Cumulez des points et profitez de réductions automatiques
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CustomerSignup;
